/**
 * AllergyChipPicker (Plan 11, Task 3 — safety-critical UI-SPEC §5).
 *
 * The merge-blocking confirmation checkbox is THE single most important
 * safety primitive in Phase 1 (PROJECT.md: severe nut + egg allergies).
 * An admin who skips allergy capture on a child with severe nuts could
 * trigger a downstream Claude-assisted food suggestion that harms them.
 *
 * Contract:
 *   - Renders all 8 `ALLERGY_CHIPS` from @stell/shared in fixed order
 *     (UI-SPEC §5) as `Chip tone="warn"` toggles (#159, steg 9f): valgt er
 *     amber `bg-warn-bg` med innfelt kant og ink-tekst (D3, Oppsett-skissen),
 *     ikke valgt er vanlig glass-chip.  Begge parene står i contrast.test.ts.
 *   - "Legg til annet…" text input appends custom chips; each custom
 *     chip is removable via × button.
 *   - Below the chips: a confirmation checkbox with copy that switches
 *     on chip count:
 *       ≥1 chip selected → `allergy_confirm_has`, som ramser opp allergiene
 *         («Jeg har sjekket: Bella tåler ikke egg og nøtter.»)
 *       0 chips selected → `allergy_confirm_none` (name-interpolated)
 *     Raden er amber til den er krysset av, og grønn etterpå.
 *   - Parent receives onChange({ chips, custom, confirmed }) every
 *     edit; the form only enables Lagre when confirmed === true.
 */
import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ALLERGY_CHIPS, type AllergyChip } from '@stell/shared';
import { Button } from '../../../components/primitives/Button';
import { Chip, chipClass } from '../../../components/primitives/Chip';
import { SectionLabel } from '../../../components/primitives/SectionLabel';

const LIST_FORMAT = new Intl.ListFormat('nb-NO', { style: 'long', type: 'conjunction' });

// Symbol glyphs used as button labels — not i18n content, but the MED-7
// lint rule treats them as string literals.  Exporting them as named
// constants satisfies the rule without changing the visual output.
const GLYPH_REMOVE = '×';
const GLYPH_ADD = '+';

export interface AllergyChipPickerValue {
  chips: AllergyChip[];
  custom: string[];
  confirmed: boolean;
}

export interface AllergyChipPickerProps {
  memberName: string;
  value: AllergyChipPickerValue;
  onChange: (next: AllergyChipPickerValue) => void;
  /**
   * Teksten i «Legg til annet…» som ikke er lagt til ennå (#166). Oppsett
   * styrer den selv, så et påbegynt medlem merkes og feltet tømmes for neste
   * medlem. Uten disse holder komponenten teksten selv.
   */
  pendingText?: string;
  onPendingTextChange?: (next: string) => void;
}

export function AllergyChipPicker({
  memberName,
  value,
  onChange,
  pendingText,
  onPendingTextChange,
}: AllergyChipPickerProps) {
  const { t } = useTranslation();
  const [ownDraft, setOwnDraft] = useState('');
  const draft = pendingText ?? ownDraft;
  const setDraft = onPendingTextChange ?? setOwnDraft;

  // Bekreftelsen nevner hver allergi, så den som krysser av ser nøyaktig
  // hva hen bekrefter (skissens «Jeg har sjekket: … tåler ikke …»).
  const picked = [
    ...ALLERGY_CHIPS.filter((c) => value.chips.includes(c)).map((c) =>
      t(`allergy.chip.${c}`).toLocaleLowerCase('nb-NO'),
    ),
    ...value.custom,
  ];
  const confirmText =
    picked.length > 0
      ? t('onboarding.step3.allergy_confirm_has', {
          name: memberName,
          list: LIST_FORMAT.format(picked),
        })
      : t('onboarding.step3.allergy_confirm_none', { name: memberName });

  function toggleChip(chip: AllergyChip) {
    const has = value.chips.includes(chip);
    const nextChips = has
      ? value.chips.filter((c) => c !== chip)
      : [...value.chips, chip];
    // Any edit to chips invalidates the confirmation — the admin MUST
    // re-tick after changing the list.  This is the safety gate: if the
    // admin toggles something accidentally, the confirmation is reset.
    onChange({ chips: nextChips, custom: value.custom, confirmed: false });
  }

  function addCustom() {
    const cleaned = draft.trim();
    if (!cleaned) return;
    if (value.custom.includes(cleaned)) {
      setDraft('');
      return;
    }
    onChange({
      chips: value.chips,
      custom: [...value.custom, cleaned],
      confirmed: false,
    });
    setDraft('');
  }

  function removeCustom(item: string) {
    onChange({
      chips: value.chips,
      custom: value.custom.filter((c) => c !== item),
      confirmed: false,
    });
  }

  // Tekst i «Legg til annet…» som ikke er lagt til ennå, er ikke med i lista
  // som bekreftes. Da kan ikke bekreftelsen stå, ellers ville allergien bli
  // borte uten at noen merket det.
  const pending = draft.trim();

  function onDraftChange(next: string) {
    setDraft(next);
    if (value.confirmed && next.trim() !== '') {
      onChange({ chips: value.chips, custom: value.custom, confirmed: false });
    }
  }

  function onDraftKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustom();
    }
  }

  function onConfirmChange(checked: boolean) {
    onChange({
      chips: value.chips,
      custom: value.custom,
      confirmed: checked,
    });
  }

  return (
    <fieldset data-testid="allergy-picker" className="m-0 flex flex-col gap-2 border-0 p-0">
      <SectionLabel as="legend">{t('onboarding.step3.allergies')}</SectionLabel>

      <div role="group" aria-label={t('onboarding.step3.allergies')} className="flex flex-wrap gap-2">
        {ALLERGY_CHIPS.map((chip) => {
          const selected = value.chips.includes(chip);
          return (
            <Chip
              key={chip}
              tone="warn"
              selected={selected}
              data-testid={`allergy-chip-${chip}`}
              data-selected={selected}
              onClick={() => toggleChip(chip)}
            >
              {t(`allergy.chip.${chip}`)}
            </Chip>
          );
        })}
        {value.custom.map((item) => (
          <span
            key={item}
            data-testid={`allergy-custom-${item}`}
            className={`${chipClass(true, 'warn')} pl-3.5`}
          >
            {item}
            <button
              type="button"
              data-testid={`allergy-custom-remove-${item}`}
              aria-label={t('common.remove_item', { item })}
              onClick={() => removeCustom(item)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-pill text-base"
            >
              {GLYPH_REMOVE}
            </button>
          </span>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          data-testid="allergy-custom-input"
          className="field min-w-0 flex-1"
          type="text"
          maxLength={40}
          aria-label={t('allergy.chip.add_other')}
          placeholder={t('allergy.chip.add_other')}
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={onDraftKey}
        />
        <Button
          variant="secondary"
          size={52}
          data-testid="allergy-custom-add"
          aria-label={t('common.add')}
          className="w-13 shrink-0 px-0 text-lg"
          onClick={addCustom}
        >
          {GLYPH_ADD}
        </Button>
      </div>

      <label
        className={`mt-1 flex cursor-pointer items-center gap-3 rounded-[18px] px-3.5 py-3 ${
          value.confirmed ? 'bg-ok-bg' : 'bg-warn-bg'
        }`}
        data-testid="allergy-confirm-row"
        data-confirmed={value.confirmed}
      >
        <input
          data-testid="allergy-confirm"
          type="checkbox"
          checked={value.confirmed}
          disabled={pending !== ''}
          onChange={(e) => onConfirmChange(e.target.checked)}
          className="m-0 h-6 w-6 shrink-0 accent-primary"
        />
        <span className="text-body-s leading-snug font-bold text-ink">{confirmText}</span>
      </label>
      {pending !== '' ? (
        <p data-testid="allergy-pending" className="m-0 text-sm text-ink-2">
          {t('allergy.chip.pending', { item: pending })}
        </p>
      ) : null}
    </fieldset>
  );
}
